import { useState, useEffect, useCallback } from 'react';
import { categoryService, Category } from '../services/categoryService';

interface UseCategoriesResult {
  categories: Category[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface UseCategoriesOptions {
  activeOnly?: boolean;
  realTime?: boolean;
}

export function useCategories(options: UseCategoriesOptions = {}): UseCategoriesResult {
  const { activeOnly = true, realTime = false } = options;
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const fetchedCategories = activeOnly 
        ? await categoryService.fetchActiveCategories()
        : await categoryService.fetchCategories();
      
      setCategories(fetchedCategories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  const refresh = useCallback(async () => {
    await fetchCategories();
  }, [fetchCategories]);

  // Initial fetch
  useEffect(() => {
    if (!realTime) {
      fetchCategories();
    }
  }, [fetchCategories, realTime]);

  // Real-time subscription
  useEffect(() => {
    if (!realTime) return;

    const unsubscribe = categoryService.subscribeToCategories((newCategories) => {
      const filteredCategories = activeOnly 
        ? newCategories.filter(category => category.isActive !== false)
        : newCategories;
      
      setCategories(filteredCategories);
      setLoading(false);
      setError(null);
    });

    return unsubscribe;
  }, [realTime, activeOnly]);

  return {
    categories,
    loading,
    error,
    refresh
  };
}

interface UseCategoryResult {
  category: Category | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useCategory(categoryId: string): UseCategoryResult {
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategory = useCallback(async () => {
    if (!categoryId) {
      setCategory(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const fetchedCategory = await categoryService.fetchCategoryById(categoryId);
      setCategory(fetchedCategory);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch category');
      setCategory(null);
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  const refresh = useCallback(async () => {
    await fetchCategory();
  }, [fetchCategory]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  return {
    category,
    loading,
    error,
    refresh
  };
}